<script setup lang="ts">
    import { computed, watch, onMounted, shallowReactive, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import { strCompareLowerCase, toStringSmartly } from '@common/Common'
    import { guid } from '@common/Guid'

    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import ResourceEditor from '@Quest/Resources/Components/ResourceEditor.vue'

    import { ResourceEditorViewModel, ResourceEditorFormSubmitEventPayload } from '@Quest/Resources/Components/ResourceEditor'
    import { ResourceReadModel, ResourcesReadModel } from '@Quest/Resources/readmodels'

    import { useResourcesByStrategyIdQueryV1 } from '@Quest/Resources/queries-vue-composables'
    import { useCreateResourceCommandV1, useUpdateResourceCommandV1, useDeleteResourceCommandV1 } from '@Quest/Resources/commands-vue-composables'


    // route
    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))


    // query
    const {
        runResourcesByStrategyIdQueryV1,
        modelResourcesByStrategyIdQueryV1
    } = useResourcesByStrategyIdQueryV1(workspaceId, strategyId)

    const rm = modelResourcesByStrategyIdQueryV1 as Ref<ResourcesReadModel | null>


    // commands
    const {
        invokeCreateResourceCommandV1,
        succeedCreateResourceCommandV1
    } = useCreateResourceCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateResourceCommandV1,
        succeedUpdateResourceCommandV1
    } = useUpdateResourceCommandV1(workspaceId, strategyId)

    const {
        invokeDeleteResourceCommandV1,
        succeedDeleteResourceCommandV1
    } = useDeleteResourceCommandV1(workspaceId, strategyId)


    // view model (stable instance)
    const resourceEditor = shallowReactive(new ResourceEditorViewModel())


    // computed
    const resourcesView = computed<ResourceReadModel[]>(() =>
        (rm.value?.Resources ?? []).slice().sort((a, b) =>
            strCompareLowerCase(a.Name, b.Name)
        )
    )


    // methods
    function fmtNum(val: number | null)
    {
        return toStringSmartly(val)
    }

    function showResourceCreator()
    {
        resourceEditor.showCreator()
    }

    function showResourceEditor(r: ResourceReadModel)
    {
        resourceEditor.showEditor(r.Id, r.Name, r.Unit, r.ExpenseClassification)
    }

    function handleResourceEditorFormSubmit(e: ResourceEditorFormSubmitEventPayload)
    {
        if (e.id == null)
        {
            invokeCreateResourceCommandV1({
                id: guid(),
                name: e.name,
                unit: e.unit,
                classification: e.classification,
                costPerUnit: e.costPerUnit
            })
        } else
        {
            invokeUpdateResourceCommandV1({
                id: e.id,
                name: e.name,
                unit: e.unit,
                classification: e.classification
            })
        }
    }

    function handleDeleteResourceCommand(r: ResourceReadModel)
    {
        if (confirm("Are you sure?") === true)
        {
            invokeDeleteResourceCommandV1({
                id: r.Id
            })
        }
    }


    // lifecycle
    onMounted(() =>
    {
        document.title = "Resources | TransparentChoice"
        runResourcesByStrategyIdQueryV1()
    })


    // command success watchers (split by concern)
    watch(succeedCreateResourceCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            runResourcesByStrategyIdQueryV1()
            resourceEditor.close()
        }
    })

    watch(succeedUpdateResourceCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            runResourcesByStrategyIdQueryV1()
            resourceEditor.close()
        }
    })

    watch(succeedDeleteResourceCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            runResourcesByStrategyIdQueryV1()
        }
    })
</script>

<template>
    <div>
        <ResourceEditor :viewModel="resourceEditor" 
                        @form-submit="handleResourceEditorFormSubmit" />

        <CorporateNavigation ActivePage="resources" />

        <div class="container-fluid" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Resources</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button role="button" class="btn btn-success" style="padding: 0.7em 2em;" @click="showResourceCreator">Create resource</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="mt-3" v-if="rm">
                <div class="toolbar">
                    <div>
                        <div class="btn-group" role="group">
                            <span class="btn btn-secondary disabled">
                                Resources
                            </span>
                            <a class="btn btn-outline-secondary" v-sanitizedHref="`/#/enterprise/resource-unit-costs/${workspaceId}/${strategyId}`">
                                Unit costs
                            </a>
                        </div>
                    </div>
                    <div></div>
                </div>

                <table class="table table-bordered table-hover">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Unit</th>
                            <!--<th>Capacity</th><th>Allocated</th>-->
                            <th>P&L</th>
                            <th>Cost per unit ({{new Date().getFullYear()}})</th>
                            <th style="width: 120px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="r in resourcesView" :key="r.Id">
                            <td>
                                <a v-sanitizedHref="`/#/enterprise/resource-capacity/${workspaceId}/${strategyId}/${r.Id}`">{{r.Name}}</a>
                            </td>

                            <td>{{r.Unit}}</td>
                            <td>{{r.ExpenseClassification}}</td>
                            <td>{{fmtNum(r.CostPerUnit)}} {{r.Currency}}</td>
                            <td>
                                <div class="dropdown">
                                    <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        Actions
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <a class="dropdown-item" @click="showResourceEditor(r)">Edit</a>
                                        <a class="dropdown-item" @click="handleDeleteResourceCommand(r)">Delete</a>
                                    </div>
                                </div>

                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </div>




    </div>

</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>


